import { useEffect, useMemo, useState } from "react";
import {
  Title,
  Text,
  Stack,
  Group,
  Paper,
  Button,
  TextInput,
  PasswordInput,
  ActionIcon,
  Badge,
  Divider,
  Tabs,
  Loader,
  Center,
  Tooltip,
  Alert,
} from "@mantine/core";
import {
  IconPlus,
  IconTrash,
  IconDeviceFloppy,
  IconAlertTriangle,
  IconKey,
  IconClock,
} from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { moduleConfigsApi } from "../../api/moduleConfigs.api";
import { apiError } from "../../api/client";

export default function ModuleConfigsPage() {
  const { data: configs = [], isLoading, error } = useQuery({
    queryKey: ["module-configs"],
    queryFn: moduleConfigsApi.list,
  });

  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    if (!activeTab && configs.length > 0) {
      setActiveTab(configs[0].moduleKey);
    }
  }, [configs, activeTab]);

  return (
    <Stack gap="lg">
      <div>
        <Title order={2}>Module Configuration</Title>
        <Text c="dimmed">
          Per-module key/value settings. Stored as JSON in the{" "}
          <code>module_configs</code> table. Admin-only.
        </Text>
      </div>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {apiError(error)}
        </Alert>
      )}

      {isLoading ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : configs.length === 0 ? (
        <Paper>
          <Text c="dimmed">No modules registered.</Text>
        </Paper>
      ) : (
        <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
          <Tabs.List>
            {configs.map((c) => (
              <Tabs.Tab key={c.moduleKey} value={c.moduleKey}>
                {c.moduleName}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {configs.map((c) => (
            <Tabs.Panel key={c.moduleKey} value={c.moduleKey} pt="md">
              <ModuleConfigEditor config={c} />
            </Tabs.Panel>
          ))}
        </Tabs>
      )}
    </Stack>
  );
}

function buildInitialEntries(configRow) {
  const schema = configRow.configSchema || [];
  const stored = configRow.config || {};
  const entries = [];

  const seen = new Set();
  for (const field of schema) {
    seen.add(field.key);
    entries.push({
      key: field.key,
      value: stored[field.key] ?? "",
      schemaField: field,
      locked: true,
    });
  }
  for (const [k, v] of Object.entries(stored)) {
    if (seen.has(k)) continue;
    entries.push({
      key: k,
      value: v ?? "",
      schemaField: null,
      locked: false,
    });
  }
  return entries;
}

function ModuleConfigEditor({ config }) {
  const qc = useQueryClient();
  const [entries, setEntries] = useState(() => buildInitialEntries(config));
  const [revealed, setRevealed] = useState(() => new Set());

  // Re-seed local state whenever the server-side config changes
  useEffect(() => {
    setEntries(buildInitialEntries(config));
    setRevealed(new Set());
  }, [config]);

  const mut = useMutation({
    mutationFn: (payload) => moduleConfigsApi.save(config.moduleKey, payload),
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: `${config.moduleName} config saved`,
      });
      qc.invalidateQueries({ queryKey: ["module-configs"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: apiError(e) }),
  });

  const duplicateKeys = useMemo(() => {
    const counts = {};
    for (const e of entries) {
      if (!e.key) continue;
      counts[e.key] = (counts[e.key] || 0) + 1;
    }
    return new Set(Object.keys(counts).filter((k) => counts[k] > 1));
  }, [entries]);

  const missingRequired = useMemo(() => {
    const out = [];
    for (const e of entries) {
      const req = e.schemaField?.required;
      if (req && (e.value === "" || e.value === null || e.value === undefined)) {
        out.push(e.key);
      }
    }
    return out;
  }, [entries]);

  function updateEntry(index, patch) {
    setEntries((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }

  function removeEntry(index) {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  }

  function addEntry() {
    setEntries((prev) => [
      ...prev,
      { key: "", value: "", schemaField: null, locked: false },
    ]);
  }

  function toggleReveal(key) {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function save() {
    if (duplicateKeys.size > 0) {
      notifications.show({
        color: "red",
        message: `Duplicate keys: ${[...duplicateKeys].join(", ")}`,
      });
      return;
    }
    const payload = {};
    for (const e of entries) {
      if (!e.key) continue;
      payload[e.key] = e.value === "" ? null : e.value;
    }
    mut.mutate(payload);
  }

  return (
    <Stack gap="md">
      <Paper>
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="xs" mb={4}>
              <Title order={4}>{config.moduleName}</Title>
              <Badge variant="light" ff="monospace">
                {config.moduleKey}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {config.moduleDescription}
            </Text>
          </div>
          <Group gap="xs">
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={save}
              loading={mut.isPending}
              disabled={duplicateKeys.size > 0}
            >
              Save
            </Button>
          </Group>
        </Group>
        {config.updatedAt && (
          <Group gap={6} mt="sm" c="dimmed">
            <IconClock size={14} />
            <Text size="xs">
              Last updated {new Date(config.updatedAt).toLocaleString()}
              {config.updatedBy
                ? ` by ${config.updatedBy.fullName || config.updatedBy.email}`
                : ""}
            </Text>
          </Group>
        )}
      </Paper>

      {missingRequired.length > 0 && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          Required key{missingRequired.length > 1 ? "s" : ""} missing:{" "}
          <strong>{missingRequired.join(", ")}</strong>
        </Alert>
      )}

      <Paper>
        <Stack gap="sm">
          {entries.length === 0 && (
            <Text c="dimmed" size="sm">
              No keys yet — click "Add key" to create one.
            </Text>
          )}

          {entries.map((entry, idx) => {
            const field = entry.schemaField;
            const isSecret = !!field?.secret;
            const isRevealed = revealed.has(entry.key);
            const isDuplicate = entry.key && duplicateKeys.has(entry.key);

            return (
              <div key={`${idx}-${entry.key}`}>
                <Group align="flex-end" gap="sm" wrap="nowrap">
                  <TextInput
                    label={
                      field ? (
                        <Group gap={4}>
                          <span>{field.label}</span>
                          {field.required && (
                            <Text component="span" c="red" size="xs">
                              *
                            </Text>
                          )}
                        </Group>
                      ) : (
                        "Key"
                      )
                    }
                    description={
                      field ? (
                        <Group gap={4}>
                          <Text component="span" ff="monospace" size="xs">
                            {entry.key}
                          </Text>
                          {field.secret && (
                            <Badge size="xs" color="orange" variant="light">
                              secret
                            </Badge>
                          )}
                        </Group>
                      ) : null
                    }
                    placeholder="key"
                    value={entry.key}
                    onChange={(e) =>
                      updateEntry(idx, { key: e.currentTarget.value })
                    }
                    disabled={entry.locked}
                    error={isDuplicate ? "duplicate key" : null}
                    style={{ flex: "0 0 240px" }}
                    leftSection={field?.secret ? <IconKey size={14} /> : null}
                  />

                  {isSecret && !isRevealed ? (
                    <PasswordInput
                      label="Value"
                      placeholder="(unchanged)"
                      value={entry.value ?? ""}
                      onChange={(e) =>
                        updateEntry(idx, { value: e.currentTarget.value })
                      }
                      onFocus={() => toggleReveal(entry.key)}
                      style={{ flex: 1 }}
                    />
                  ) : (
                    <TextInput
                      label="Value"
                      placeholder=""
                      value={entry.value ?? ""}
                      onChange={(e) =>
                        updateEntry(idx, { value: e.currentTarget.value })
                      }
                      style={{ flex: 1 }}
                    />
                  )}

                  <Tooltip
                    label={
                      entry.locked
                        ? "Schema key — value can be edited but the key is fixed"
                        : "Remove this key"
                    }
                  >
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="lg"
                      disabled={entry.locked}
                      onClick={() => removeEntry(idx)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                {field?.description && (
                  <Text size="xs" c="dimmed" mt={4} ml={2}>
                    {field.description}
                  </Text>
                )}
                <Divider mt="sm" variant="dashed" />
              </div>
            );
          })}

          <Group>
            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={addEntry}
            >
              Add key
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
